import {
  CircularProgress,
  List,
  ListItemButton,
  ListItemButtonProps,
  ListItemSecondaryAction,
  ListItemText,
  ListProps,
  Stack,
  Tooltip
} from '@mui/material'
import { Check, Warning } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Device } from '../../types'
import { OnomondoSim } from '../../types/Onomondo'
import { getDeviceSim } from '../../services/deviceService'
import { usePolicy } from '../../contexts/PolicyContext'
import { getLatestDeviceFirmwareVersion, getLatestOtaFirmwareVersion } from '../../services/ota'
import { DeviceType, OtaFirmwareVersionType } from '../../enums'
import DeviceUpdateFirmwareVersionButton from '../DeviceUpdateFirmwareVersionButton'
import DeviceUpdateTypeButton from '../DeviceUpdateTypeButton'

interface SimListItemProps extends Omit<ListItemButtonProps, 'children'> {
  label: string
  device: Device
}

function SimListItem({ label, device, ...props }: SimListItemProps) {
  const { t } = useTranslation()

  const [sim, setSim] = useState<OnomondoSim | null>(null)

  const [loading, setLoading] = useState<boolean>(true)

  const loadSim = async (name: string) => {
    try {
      setLoading(true)
      setSim(await getDeviceSim(name))
    } catch {
      // Ignore the error.
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (device.simId) {
      loadSim(device.name)
    } else {
      setLoading(false)
    }
  }, [device.name, device.simId])

  return (
    <ListItemButton {...props}>
      <ListItemText primary={device.simId ?? '-'} secondary={label} />
      <ListItemSecondaryAction>
        {loading ? (
          <CircularProgress size={20} />
        ) : !sim ? (
          <Tooltip title={t('simCardNotFound')}>
            <Warning color="warning" />
          </Tooltip>
        ) : !sim.activated ? (
          <Tooltip title={t('simCardNotActivated')}>
            <Warning color="warning" />
          </Tooltip>
        ) : !sim.online ? (
          <Tooltip title={t('simCardOffline')}>
            <Warning color="warning" />
          </Tooltip>
        ) : (
          <Tooltip title={t('simCardConnectionOk')}>
            <Check color="success" />
          </Tooltip>
        )}
      </ListItemSecondaryAction>
    </ListItemButton>
  )
}

interface FirmwareVersionListItemProps extends Omit<ListItemButtonProps, 'children'> {
  label: string
  device: Device
  firmwareType?: OtaFirmwareVersionType
  onDeviceChange?: (device: Device) => void
}

function FirmwareVersionListItem({
  label,
  device,
  firmwareType,
  onDeviceChange,
  ...props
}: FirmwareVersionListItemProps) {
  const { t } = useTranslation()

  const [latestFirmwareVersion, setLatestFirmwareVersion] = useState<string | null>(null)

  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const [loading, setLoading] = useState<boolean>(true)

  // The device type matching the given firmware type.
  const type =
    firmwareType === undefined
      ? undefined
      : firmwareType === OtaFirmwareVersionType.VALVE
        ? DeviceType.VALVE
        : DeviceType.SPINE

  const loadLatestFirmwareVersion = async () => {
    try {
      setLoading(true)
      setLatestFirmwareVersion(
        firmwareType !== undefined
          ? await getLatestOtaFirmwareVersion(firmwareType)
          : await getLatestDeviceFirmwareVersion(device.type)
      )
    } catch {
      // Ignore the error.
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadLatestFirmwareVersion()
  }, [firmwareType, device.type])

  return (
    <ListItemButton {...props}>
      <ListItemText primary={device.firmwareVersion ?? '-'} secondary={label} />
      <ListItemSecondaryAction>
        {loading ? (
          <CircularProgress size={20} />
        ) : latestFirmwareVersion === null ? (
          <Tooltip title={t('latestFirmwareVersionNotFound')}>
            <Warning color="warning" />
          </Tooltip>
        ) : type !== undefined && device.type !== type ? (
          <Stack direction="row" alignItems="center" spacing={4}>
            <DeviceUpdateTypeButton device={device} type={type} onDeviceChange={onDeviceChange} />
            <Tooltip title={t('latestFirmwareVersion', { version: latestFirmwareVersion })}>
              <Warning color="warning" />
            </Tooltip>
          </Stack>
        ) : device.firmwareVersion === latestFirmwareVersion ? (
          <Check color="success" />
        ) : (
          <Stack direction="row" alignItems="center" spacing={4}>
            <DeviceUpdateFirmwareVersionButton
              device={device}
              latestFirmwareVersion={latestFirmwareVersion}
              firmwareType={firmwareType}
              onDeviceChange={onDeviceChange}
              onUpdatingChange={setIsUpdating}
            />
            {!isUpdating && (
              <Tooltip title={t('latestFirmwareVersion', { version: latestFirmwareVersion })}>
                <Warning color="warning" />
              </Tooltip>
            )}
          </Stack>
        )}
      </ListItemSecondaryAction>
    </ListItemButton>
  )
}

export interface DeviceInstallationDetailsListProps extends Omit<ListProps, 'children'> {
  device: Device
  firmwareType?: OtaFirmwareVersionType
  onDeviceChange?: (device: Device) => void
}

export default function DeviceInstallationDetailsList({
  device,
  firmwareType,
  onDeviceChange,
  ...props
}: DeviceInstallationDetailsListProps) {
  const { t } = useTranslation('installation')

  const { isSuperadmin, isManufacturer } = usePolicy()

  return (
    <List {...props}>
      <ListItemButton>
        <ListItemText primary={device.shortUUID} secondary={t('device:shortUUID')} />
      </ListItemButton>

      <ListItemButton>
        <ListItemText primary={device.name} secondary={t('device:name')} />
      </ListItemButton>

      {(isSuperadmin || isManufacturer) && (
        <SimListItem label={t('device:simId')} device={device} />
      )}

      <FirmwareVersionListItem
        label={t('device:firmwareVersion')}
        device={device}
        firmwareType={firmwareType}
        onDeviceChange={onDeviceChange}
      />

      <ListItemButton>
        <ListItemText
          primary={device.hardwareVersion ?? '-'}
          secondary={t('device:hardwareVersion')}
        />
      </ListItemButton>
    </List>
  )
}
